import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabaseClient } from '@/lib/supabase';

import {
  defaultValues,
  type FormValues,
  validationSchema,
} from './validationSchema';

interface Props {
  dialogOpen: boolean;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
}

export const UpdatePasswordDialog = ({
  dialogOpen,
  setDialogOpen,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['auth', 'update-password'],
    mutationFn: async (values: FormValues) => {
      const { error } = await supabaseClient.auth.updateUser({
        password: values.password,
      });

      if (error !== null) {
        console.error('Error updating password:', error.message);

        throw error;
      }
    },
    onSuccess: () => {
      setDialogOpen(false);
      toast({
        title: 'profile.updatePassword.form.submit.success',
        variant: 'default',
      });
    },
    onError: () => {
      toast({
        title: 'profile.updatePassword.form.submit.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage id="profile.updatePassword.title" />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage id="profile.updatePassword.subtitle" />
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(values => mutateAsync(values))}>
            <div className="space-y-2 pb-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>
                      <FormattedMessage id="profile.updatePassword.form.password.label" />
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                          placeholder={formatMessage({
                            id: 'profile.updatePassword.form.password.placeholder',
                          })}
                          type={showPassword ? 'text' : 'password'}
                        />
                        <Button
                          type="button"
                          onClick={() => setShowPassword(s => !s)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                        >
                          {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button type="submit" variant="main" disabled={isPending}>
                {isPending ? (
                  <FormattedMessage id="profile.updatePassword.form.submit.pending" />
                ) : (
                  <FormattedMessage id="profile.updatePassword.form.submit.label" />
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
